import { useId, useState, type Ref } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { generatedInstanceName, validateAppName, validateDisplayName, validateExternalUserId } from '@/lib/deployPlan';
import { subdomainError } from '@/lib/subdomain';
import { EnvVarEditor, type EnvDraft } from './EnvVarEditor';

export interface ConfigureValues {
  appName: string;
  externalUserId: string;
  displayName: string;
  useCustomName: boolean;
  instanceName: string;
  subdomain: string;
}

export type ConfigureField = 'appName' | 'externalUserId' | 'displayName' | 'instanceName';

export interface ConfigureStepProps {
  values: ConfigureValues;
  onChangeField: (field: ConfigureField, value: string) => void;
  onToggleCustomName: (value: boolean) => void;
  onChangeSubdomain: (value: string) => void;
  /** The app gets a hostname (a domain exists and the catalog exposes a port), so a subdomain can be chosen. */
  subdomainAvailable: boolean;
  /** Reveal every field's error (set by the page after a failed Next attempt). */
  showErrors: boolean;
  catalogName: string;
  /** Captured once by the page; used for the generated-name preview. */
  nowMs: number;
  envVars: Readonly<Record<string, string>>;
  envDefaults: Readonly<Record<string, string>>;
  onSetEnv: (key: string, value: string) => void;
  onAddEnv: (key: string, value: string) => void;
  onRemoveEnv: (key: string) => void;
  envDraft: EnvDraft;
  onEnvDraftChange: (draft: EnvDraft) => void;
  headingRef?: Ref<HTMLHeadingElement>;
}

const NAME_RULE = 'Use lowercase letters, numbers and hyphens (max 64), not starting or ending with a hyphen';

type InputField = ConfigureField | 'subdomain';

function errorFor(field: InputField, v: ConfigureValues): string | null {
  switch (field) {
    case 'subdomain':
      return subdomainError(v.subdomain);
    case 'externalUserId':
      if (!v.externalUserId) return 'Required';
      return validateExternalUserId(v.externalUserId) ? null : 'Use letters, numbers, underscores and dashes only (max 128)';
    case 'displayName':
      return validateDisplayName(v.displayName) ? null : 'Use letters and spaces only (max 255)';
    case 'appName':
      return !v.appName || validateAppName(v.appName) ? null : NAME_RULE;
    case 'instanceName':
      if (!v.useCustomName) return null;
      if (!v.instanceName) return 'Required';
      return validateAppName(v.instanceName) ? null : NAME_RULE;
  }
}

/** Use as the third wizard step: identify the user, name the deployment, and set env vars. */
export function ConfigureStep({
  values,
  onChangeField,
  onToggleCustomName,
  onChangeSubdomain,
  subdomainAvailable,
  showErrors,
  catalogName,
  nowMs,
  envVars,
  envDefaults,
  onSetEnv,
  onAddEnv,
  onRemoveEnv,
  envDraft,
  onEnvDraftChange,
  headingRef,
}: ConfigureStepProps) {
  const uid = useId();
  // Errors appear once a field has been blurred, or for every field when showErrors is set.
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const touch = (f: string) => setTouched((prev) => (prev.has(f) ? prev : new Set(prev).add(f)));

  const generated = generatedInstanceName(catalogName, values.externalUserId, nowMs);

  const field = (
    f: InputField,
    label: string,
    help: string,
    opts: { placeholder: string; required?: boolean; lower?: boolean },
  ) => {
    const id = `${uid}-${f}`;
    const err = showErrors || touched.has(f) ? errorFor(f, values) : null;
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id}>
          {label}
          {opts.required && <span className="text-muted-foreground"> (required)</span>}
        </Label>
        <Input
          id={id}
          value={values[f]}
          onChange={(e) => (f === 'subdomain' ? onChangeSubdomain(e.target.value) : onChangeField(f, e.target.value))}
          onBlur={() => touch(f)}
          placeholder={opts.placeholder}
          autoComplete="off"
          spellCheck={false}
          required={opts.required}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-help ${id}-err` : `${id}-help`}
        />
        <p id={`${id}-help`} className="text-xs text-muted-foreground">
          {help}
        </p>
        {err && (
          <p id={`${id}-err`} className="text-xs font-medium text-destructive">
            {err}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h2 ref={headingRef} tabIndex={-1} className="text-lg font-semibold outline-none">
        Configure your app
      </h2>

      <div className="space-y-4">
        {field('externalUserId', 'External user ID', 'Alphanumeric, underscores, dashes. Max 128 chars', {
          placeholder: 'user123',
          required: true,
        })}
        {field('displayName', 'Display name', "The user's human-readable name. Letters and spaces only.", { placeholder: 'John Doe' })}
        {field('appName', 'App name', 'Custom name for this deployment (auto-generated from catalog name if empty)', {
          placeholder: 'my-app',
        })}

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              id={`${uid}-custom`}
              type="checkbox"
              checked={values.useCustomName}
              onChange={(e) => onToggleCustomName(e.target.checked)}
              className="size-4 rounded-sm accent-primary outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <Label htmlFor={`${uid}-custom`}>Use custom instance name</Label>
          </div>
          {values.useCustomName && field('instanceName', 'Instance name', 'The exact name of this instance.', {
            placeholder: generated || 'my-instance',
            required: true,
          })}
          <p className="text-xs text-muted-foreground">
            Generated name: <span className="font-mono">e.g. {generated || '…'}</span>
          </p>
        </div>

        {subdomainAvailable &&
          field('subdomain', 'Subdomain', 'Leave empty for a generated name. If the name is taken, the app gets <name>-<deployment id>.', {
            placeholder: 'my-shop',
          })}
      </div>

      <EnvVarEditor
        envVars={envVars} defaults={envDefaults} onSet={onSetEnv} onAdd={onAddEnv} onRemove={onRemoveEnv}
        draft={envDraft}
        onDraftChange={onEnvDraftChange}
      />
    </div>
  );
}
